import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface ClassSizeEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentCapacity: string | number;
    onSave: (newCapacity: string) => void;
    sessionTitle?: string;
}

const ClassSizeEditModal: React.FC<ClassSizeEditModalProps> = ({
    visible,
    onClose,
    currentCapacity,
    onSave,
    sessionTitle = "Session"
}) => {
    const [capacity, setCapacity] = useState(currentCapacity?.toString() || '');
    const [error, setError] = useState('');

    // Update state when modal opens or currentCapacity changes
    useEffect(() => {
        if (visible) {
            setCapacity(currentCapacity?.toString() || '');
            setError('');
        }
    }, [visible, currentCapacity]);

    const validateCapacity = (value: string) => {
        if (!value.trim()) {
            return 'Class size is required';
        }
        const num = parseInt(value);
        if (isNaN(num)) {
            return 'Class size must be a number';
        }
        if (num <= 0) {
            return 'Class size must be greater than 0';
        }
        if (num > 1000) {
            return 'Class size cannot exceed 1000';
        }
        return '';
    };

    const handleCapacityChange = (text: string) => {
        // Only allow digits
        const numericText = text.replace(/[^0-9]/g, '');
        setCapacity(numericText);
        setError(validateCapacity(numericText));
    };

    const isValid = !error && capacity.trim() !== '';

    const handleSave = () => {
        const validationError = validateCapacity(capacity);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSave(capacity);
        onClose();
    };

    const handleCancel = () => {
        setCapacity(currentCapacity?.toString() || '');
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{sessionTitle || 'Edit Class Size'}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>Maximum Participants</Text>
                        <TextInput
                            style={[styles.input, error ? styles.inputError : null]}
                            value={capacity}
                            onChangeText={handleCapacityChange}
                            placeholder="Enter class size (e.g., 20)"
                            placeholderTextColor="#adb5bd"
                            keyboardType="numeric"
                            autoFocus={true}
                            selectTextOnFocus={true}
                        />
                        
                        {error ? (
                            <Text style={styles.errorText}>{error}</Text>
                        ) : (
                            <Text style={styles.helperText}>
                                Set the maximum number of participants for this session
                            </Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 8,
    },
    input: {
        borderWidth: 2,
        borderColor: '#e9ecef',
        borderRadius: 8,
        padding: 12,
        fontSize: 16,
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
    },
    inputError: {
        borderColor: '#dc3545',
        backgroundColor: '#fff5f5',
    },
    helperText: {
        fontSize: 12,
        color: '#666',
        marginTop: 8,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 8,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default ClassSizeEditModal; 